import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { debounceTime, switchMap, map, catchError, of, timer, Observable } from 'rxjs';
import { SignupAPIService } from '../services/signup-apiservice';
import { HttpClient } from '@angular/common/http';
import { FormDataService } from '../services/form-data-service';
import { validatePhoneNumber } from '../utils/phone-numbers';

function isApiTrue(value: boolean | string | undefined | null): boolean {
  return value === true || String(value).toLowerCase() === 'true';
}

/**
 * ✅ Domain format validator (sync)
 * Ensures the value looks like a valid domain name (e.g. example.com).
 * Rejects values starting with www., http://, https://.
 */
export function domainNameValidatorRegex(): ValidatorFn {
  // no http, https, www, ://
  const domainRegex = /^(?!https?:\/\/)(?!www\.)(?!.*:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim();
    if (!value) return null;
    return domainRegex.test(value) ? null : { invalidDomain: true };
  };
}

export function office365BaseNameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value ?? '').trim();
    if (!v) return null;
    if (!/^[a-zA-Z0-9-]{3,27}$/.test(v)) return { invalidOfficeName: true };
    if (v.includes('.')) return { invalidOfficeName: true };
    return null;
  };
}

/**
 * ✅ Strong password validator (sync)
 * Requires 10–16 characters, at least one uppercase, one lowercase, one digit,
 * and allows a specific set of special characters.
 */
export function strongPasswordValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d+\-_,.~()\[\]{}<>$#%&/=?!|@^"':;]{10,16}$/;
  return regex.test(control.value) ? null : { strongPassword: true };
}

/**
 * ✅ Email format validator (sync)
 * Requires a valid email format with proper TLD (e.g., user@domain.com).
 * Dots are allowed in the local part only when not leading, trailing, or consecutive.
 */
export function emailFormatValidator(): ValidatorFn {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim();
    if (!value) return null;

    if (!emailRegex.test(value)) {
      return { invalidEmail: true };
    }

    const atIndex = value.lastIndexOf('@');
    const localPart = value.slice(0, atIndex);
    const domainPart = value.slice(atIndex + 1);

    const hasInvalidDots = (part: string) =>
      part.startsWith('.') || part.endsWith('.') || part.includes('..');

    if (hasInvalidDots(localPart) || hasInvalidDots(domainPart)) {
      return { invalidEmail: true };
    }

    return null;
  };
}

/** Validates phone numbers against the selected country dial code and length rules. */
export function phoneNumberValidator(formData?: FormDataService): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return null;

    const meta = formData?.getPhoneMeta();
    const result = validatePhoneNumber(raw, {
      countryCode: meta?.phoneCountryCode,
      dialCode: meta?.phoneDialCode,
    });

    return result.valid ? null : { phoneFormat: true };
  };
}

/**
 * ✅ Username validator (async)
 * Calls the SignupAPIService to check if a username already exists.
 * Returns { usernameTaken: true } if unavailable.
 */
export function usernameValidator(api: SignupAPIService): AsyncValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return of(null);

    // debounce using timer instead of valueChanges
    return timer(400).pipe(
      switchMap(() =>
        api.validateUsername(control.value).pipe(
          map(res => {
            return res.exist ? { usernameTaken: true } : null;
          }),
          catchError(() => of({ usernameCheckFailed: true }))
        )
      )
    );
  };
}

/**
 * ✅ Domain availability validator (async)
 * - Normalizes input (removes http(s):// and www.)
 * - Ensures format is sld.tld (e.g. example.com)
 * - Debounced using timer() to avoid multiple calls while typing
 * - Returns { invalidDomain: true } if format is invalid
 * - Returns { domainTaken: true } if the domain is already registered
 * - Returns { domainCheckFailed: true } if the API call fails
 */
export function domainValidatorApps(api: SignupAPIService): AsyncValidatorFn {
  return (control: AbstractControl) => {
    const raw = (control.value ?? '').trim();
    if (!raw) return of(null);

    if (
      raw.includes('http://') ||
      raw.includes('https://') ||
      raw.includes('://') ||
      raw.startsWith('www.')
    ) {
      return of({ invalidDomain: true });
    }

    const host = raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    const parts = host.split('.');

    if (parts.length < 2) {
      return of({ invalidDomain: true });
    }

    const tld = parts.pop();
    const sld = parts.pop();
    const snapshot = host;

    return timer(400).pipe(
      switchMap(() => {
        const current = (control.value ?? '')
          .trim()
          .replace(/^https?:\/\//i, '')
          .replace(/^www\./i, '');

        if (current !== snapshot) return of(null);

        return api.validateDomainApps(sld!, tld!).pipe(
          map((res) => (isApiTrue(res.domainAvailable) ? null : { domainTaken: true })),
          catchError(() => of({ domainCheckFailed: true }))
        );
      })
    );
  };
}


export function office365DomainValidator(api: SignupAPIService): AsyncValidatorFn {
  return (control: AbstractControl) => {
    const domain = (control.value ?? '').trim();
    if (!domain) return of(null);

    return timer(400).pipe(
      switchMap(() =>
        api.validateDomainOffice365(domain).pipe(
          map((res) => (isApiTrue(res.domainTaken) ? { domainTaken: true } : null)),
          catchError(() => of({ domainCheckFailed: true }))
        )
      )
    );
  };
}

/**
 * Domain white space validator
 * any blank/white space in doamin dame it returns error
 */
export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null) return null;
    return String(value).trim().length === 0 ? { required: true } : null;
  };
}

/**
 * ✅ Load domain extra attributes (e.g. .us specific requirements)
 * Reads `assets/data/domainExtraAttributes.json` and returns the parsed object.
 */
export function loadDomainExtraAttributes(http: HttpClient): Observable<any> {
  return http.get<any>('assets/data/domainExtraAttributes.json').pipe(
    catchError(() => of({ attribute: [] }))
  );
}

/**
 * ✅ Passwords match validator (sync, cross-field)
 * Compares `password` and `confirmPassword` controls.
 * Returns { passwordMismatch: true } if they are different.
 */
export function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const pass = group.get('password')?.value;
  const confirm = group.get('confirmPassword')?.value;
  return pass === confirm ? null : { passwordMismatch: true };
}

/**
 * ✅ Email verification validator (async)
 * Handles sending email and managing the sessionId/timer logic.
 * Returns { emailNotVerified: true } if not verified yet.
 */
export function emailVerificationValidator(api: SignupAPIService): AsyncValidatorFn {
  return (control: AbstractControl) => {
    const email = control.value?.trim();
    if (!email) return of(null);

    // debounce before hitting API
    return timer(400).pipe(
      switchMap(() =>
        api.validateEmail(email).pipe(
          map((res) => {
            // Save sessionId in the control for later use (e.g., passcode verification)
            (control as any).sessionId = res.sessionId;
            (control as any).expireInSeconds = res.expireInSeconds;
            return null;
          }),
          catchError(() => of({ emailCheckFailed: true }))
        )
      )
    );
  };
}

/**
 * ✅ Passcode verification validator (async)
 * Uses stored sessionId from email validator.
 * Returns { invalidPasscode: true } if verification fails.
 */
export function passcodeValidator(api: SignupAPIService, emailControl: AbstractControl): AsyncValidatorFn {
  return (control: AbstractControl) => {
    const code = control.value?.trim();
    const email = emailControl.value?.trim();
    const sessionId = (emailControl as any).sessionId;

    if (!code || !email || !sessionId) return of({ emailNotVerified: true });

    return api.verifyPasscode(sessionId, email, code).pipe(
      map(() => null),
      catchError(() => of({ invalidPasscode: true }))
    );
  };
}
